import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import ParticleBackground from "@/components/ParticleBackground";
import AIGrid from "@/components/AIGrid";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Shield, Lock, FileCheck, Download, CheckCircle, Award } from "lucide-react";
import { Link } from "react-router-dom";
import BrandLogo from "@/components/BrandLogo";
import { motion } from "framer-motion";

const Compliance = () => {
  const certifications = [
    {
      name: "GDPR",
      fullName: "General Data Protection Regulation",
      abbr: "GDPR",
      logoUrl: "https://upload.wikimedia.org/wikipedia/commons/6/6a/GDPR-logo.svg",
      status: "Fully Compliant Since 2023",
      description: "Complete compliance with European data protection regulations ensuring customer data privacy and control.",
      highlights: ["Right to be forgotten", "Data portability", "Breach notification", "Privacy by design"],
      colors: "from-blue-500 to-cyan-500"
    },
    {
      name: "EU AI Act",
      fullName: "European AI Act Compliance",
      abbr: "EU AI",
      logoUrl: "https://upload.wikimedia.org/wikipedia/commons/b/b7/European_Union_circular.svg",
      status: "Ready for 2025 Implementation",
      description: "Transparent, accountable AI systems meeting new EU standards for high-risk AI applications.",
      highlights: ["Transparency requirements", "Human oversight", "Risk management", "Technical documentation"],
      colors: "from-purple-500 to-blue-500"
    },
    {
      name: "HIPAA",
      fullName: "Health Insurance Portability & Accountability Act",
      abbr: "HIPAA",
      logoUrl: "https://upload.wikimedia.org/wikipedia/commons/9/9a/Caduceus.svg",
      status: "Healthcare-Ready Certification",
      description: "Secure PHI handling for healthcare applications with Business Associate Agreements available.",
      highlights: ["PHI encryption", "Access controls", "Audit trails", "BAA available"],
      colors: "from-green-500 to-teal-500"
    },
    {
      name: "DORA",
      fullName: "Digital Operational Resilience Act",
      abbr: "DORA",
      logoUrl: "https://upload.wikimedia.org/wikipedia/commons/b/b7/European_Union_circular.svg",
      status: "Financial Services Ready",
      description: "Operational resilience for EU financial institutions with ICT risk management compliance.",
      highlights: ["ICT risk management", "Incident reporting", "Resilience testing", "Third-party oversight"],
      colors: "from-amber-500 to-orange-500"
    },
    {
      name: "SOC 2 Type I",
      fullName: "Service Organization Control 2 Type I",
      abbr: "SOC2 I",
      logoUrl: "https://upload.wikimedia.org/wikipedia/commons/5/5e/AICPA_SOC_logo.png",
      status: "Certified 2024",
      description: "Security controls independently verified by accredited third-party auditors.",
      highlights: ["Security", "Availability", "Confidentiality", "Point-in-time assessment"],
      colors: "from-indigo-500 to-purple-500"
    },
    {
      name: "SOC 2 Type II",
      fullName: "Service Organization Control 2 Type II",
      abbr: "SOC2 II",
      logoUrl: "https://upload.wikimedia.org/wikipedia/commons/5/5e/AICPA_SOC_logo.png",
      status: "Certified 2024 - 6 Month Audit",
      description: "Sustained security compliance over extended period proving operating effectiveness.",
      highlights: ["6-month audit period", "Operating effectiveness", "Continuous monitoring", "Detailed reporting"],
      colors: "from-violet-500 to-purple-600"
    },
    {
      name: "ISO 27001",
      fullName: "Information Security Management",
      abbr: "ISO 27001",
      logoUrl: "https://upload.wikimedia.org/wikipedia/commons/6/6e/ISO_Logo_%28red_square%29.svg",
      status: "Certified 2023",
      description: "International information security management system standard certification.",
      highlights: ["ISMS framework", "Risk assessment", "Security controls", "Continuous improvement"],
      colors: "from-blue-600 to-indigo-600"
    },
    {
      name: "PCI DSS",
      fullName: "Payment Card Industry Data Security Standard",
      abbr: "PCI DSS",
      logoUrl: "https://upload.wikimedia.org/wikipedia/commons/4/46/PCI_SSC_Logo.png",
      status: "Level 1 Service Provider",
      description: "Highest level payment card data protection with quarterly security scans passed.",
      highlights: ["Secure network", "Card data protection", "Vulnerability management", "Quarterly scans"],
      colors: "from-red-500 to-pink-600"
    },
  ];

  const timeline = [
    { year: "2025", event: "ISO 27001 Certification Achieved" },
    { year: "2025", event: "GDPR Full Compliance Implemented" },
    { year: "2025", event: "SOC 2 Type I & Type II Certified" },
    { year: "2025", event: "PCI DSS Level 1 Certification" },
    { year: "2025", event: "HIPAA Compliance & BAA Framework" },
    { year: "2025", event: "EU AI Act Readiness (Ongoing)" },
    { year: "2025", event: "DORA Compliance for Financial Services" },
  ];

  const securityMetrics = [
    { metric: "0", label: "Data Breaches", color: "text-green-500" },
    { metric: "100%", label: "Uptime SLA", color: "text-blue-500" },
    { metric: "256-bit", label: "Encryption Standard", color: "text-purple-500" },
    { metric: "24/7", label: "Security Monitoring", color: "text-cyan-500" },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background relative overflow-hidden">
      <ParticleBackground />
      <AIGrid />
      <Navigation />
      
      <main className="flex-grow pt-32 pb-20 relative z-10">
        <div className="container mx-auto px-4">
          {/* Hero Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-20"
          >
            <div className="inline-flex items-center gap-3 mb-6 px-6 py-3 rounded-full glass-effect border border-primary/30">
              <Shield className="w-6 h-6 text-primary" />
              <span className="font-semibold">Enterprise Security & Regulatory Compliance</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-8 tracking-tight">
              Built to Meet the <span className="gradient-brand-text">Strictest Global Standards</span>
            </h1>
            <p className="text-2xl text-muted-foreground max-w-4xl mx-auto leading-relaxed">
              Comprehensive data protection and AI governance compliance for regulated industries worldwide
            </p>
          </motion.div>

          {/* Security Metrics */}
          <div className="grid md:grid-cols-4 gap-8 mb-24">
            {securityMetrics.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="h-full"
              >
                <Card className="p-12 glass-effect border-primary/40 text-center hover-lift group h-full flex flex-col items-center justify-center">
                  <div className={`text-7xl font-bold mb-4 ${item.color} group-hover:scale-110 transition-transform`}>{item.metric}</div>
                  <div className="text-lg text-muted-foreground font-bold text-balance">{item.label}</div>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Certifications Grid */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-20"
          >
            <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold mb-20 text-center tracking-tight">
              Certifications & <span className="gradient-brand-text">Standards</span>
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
              {certifications.map((cert, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="p-12 glass-effect border-primary/40 hover:shadow-2xl hover-lift transition-all duration-500 h-full group">
                    <div className="text-center mb-8">
                      <div className="mb-6 group-hover:scale-105 transition-transform duration-500">
                        <div className="flex items-center justify-center h-20">
                          <BrandLogo 
                            name={cert.abbr}
                            localSrc={`/assets/logos/compliance/${cert.abbr.toLowerCase().replace(/\s+/g,'-')}.svg`}
                            externalSrc={cert.logoUrl}
                          />
                        </div>
                      </div>
                      <h3 className="text-3xl font-bold mb-3 tracking-tight">{cert.name}</h3>
                      <p className="text-sm text-muted-foreground mb-4">{cert.fullName}</p>
                      <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-green-500/20 border-2 border-green-500/40 text-green-400 text-sm font-bold">
                        <CheckCircle className="w-5 h-5" />
                        {cert.status}
                      </span>
                    </div>
                    
                    <p className="text-muted-foreground mb-8 leading-relaxed text-base text-center">{cert.description}</p>
                    
                    <div className="pt-6 border-t border-border">
                      <h4 className="font-bold text-sm mb-4 text-center">Key Features:</h4>
                      <ul className="space-y-3">
                        {cert.highlights.map((highlight, idx) => (
                          <li key={idx} className="flex items-start gap-3 text-sm">
                            <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                            <span>{highlight}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Compliance Timeline */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-20"
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-16 text-center tracking-tight">
              Compliance <span className="gradient-brand-text">Journey</span>
            </h2>
            <Card className="p-8 md:p-12 glass-effect border-primary/20">
              <div className="space-y-6">
                {timeline.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center gap-6 pb-6 border-b border-border/30 last:border-0"
                  >
                    <div className="flex-shrink-0">
                      <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                        <span className="text-white font-bold text-lg">{item.year}</span>
                      </div>
                    </div>
                    <div className="flex-grow">
                      <p className="text-lg font-semibold">{item.event}</p>
                    </div>
                    <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
                  </motion.div>
                ))}
              </div>
            </Card>
          </motion.div>

          {/* Security Architecture */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-20"
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-16 text-center tracking-tight">
              Multi-Layer <span className="gradient-brand-text">Security Architecture</span>
            </h2>
            <Card className="p-10 glass-effect border-primary/20">
              <div className="grid md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center mx-auto mb-4">
                    <Lock className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-bold mb-3">End-to-End Encryption</h3>
                  <p className="text-sm text-muted-foreground">256-bit AES encryption for all data at rest and in transit</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mx-auto mb-4">
                    <Shield className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-bold mb-3">Role-Based Access</h3>
                  <p className="text-sm text-muted-foreground">Granular permission controls with comprehensive audit trails</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center mx-auto mb-4">
                    <FileCheck className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-bold mb-3">Continuous Monitoring</h3>
                  <p className="text-sm text-muted-foreground">24/7 security monitoring with real-time threat detection</p>
                </div>
              </div>
            </Card>
          </motion.div>

          {/* Download Center */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-20"
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-16 text-center tracking-tight">
              Compliance <span className="gradient-brand-text">Documentation</span>
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                { title: "Security Whitepaper", desc: "Comprehensive overview of our security architecture", icon: FileCheck },
                { title: "SOC 2 Type II Report", desc: "Available under NDA for enterprise customers", icon: Award },
                { title: "GDPR Compliance Guide", desc: "How we handle European customer data", icon: Shield },
                { title: "Penetration Test Summary", desc: "Latest third-party security assessment results", icon: Lock },
              ].map((doc, index) => (
                <Card key={index} className="p-6 glass-effect border-primary/20 hover:shadow-xl transition-all cursor-pointer group">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-primary to-accent flex items-center justify-center group-hover:scale-110 transition-transform">
                      <doc.icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-grow">
                      <h3 className="font-bold mb-1">{doc.title}</h3>
                      <p className="text-sm text-muted-foreground">{doc.desc}</p>
                    </div>
                    <Download className="w-5 h-5 text-primary group-hover:translate-y-1 transition-transform" />
                  </div>
                </Card>
              ))}
            </div>
          </motion.div>

          {/* CTA Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Card className="p-16 glass-effect border-primary/30 bg-gradient-to-br from-primary/20 to-accent/20 text-center hover-lift">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 tracking-tight">Questions About Our Compliance?</h2>
              <p className="text-xl text-muted-foreground mb-10 max-w-3xl mx-auto leading-relaxed">
                Our compliance team is ready to answer your questions and provide additional documentation
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact">
                  <Button size="lg" className="gradient-brand text-white hover:opacity-90">
                    Contact Compliance Team
                  </Button>
                </Link>
                <Link to="/platform">
                  <Button size="lg" variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white">
                    View Platform Security
                  </Button>
                </Link>
              </div>
            </Card>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Compliance;